import React, { useEffect, useState } from "react";
import {
  Box,
  Container,
  Card,
  CardContent,
  Typography,
  Button,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Chip,
  Tooltip,
  Snackbar,
  Alert as MuiAlert,
} from "@mui/material";
import { AddRounded, EditRounded, DeleteRounded } from "@mui/icons-material";
import { ThemeProvider, createTheme, alpha } from "@mui/material/styles";
import { vendorAPI } from "../services/api";
import { useAuth } from "../context/AuthContext";

const getTheme = () =>
  createTheme({
    palette: {
      mode: "light",
      primary: { main: "#005b82", light: "#2586bc", dark: "#003d5c" },
      background: { default: "#f8fafc", paper: "#ffffff" },
    },
    shape: { borderRadius: 12 },
  });

// Default form state
const emptyVendorForm = {
  sr_no: "",
  company_name: "",
  vendor_code: "",
  website: "",
  addresses: [{ address: "", is_primary: true }],
  contacts: [{ contact_name: "", phone: "", is_primary: true }],
  emails: [{ email: "", is_primary: true }],
};

const VendorTracker = () => {
  const theme = getTheme();
  const { canCreateModule, canUpdateModule, canDeleteModule } = useAuth();

  const [vendors, setVendors] = useState([]);
  const [open, setOpen] = useState(false);
  const [vendorForm, setVendorForm] = useState(emptyVendorForm);
  const [editingId, setEditingId] = useState(null);

  const perms = {
    canCreate: () => canCreateModule("vendor"),
    canEdit: () => canUpdateModule("vendor"),
    canDelete: () => canDeleteModule("vendor"),
  };

  /** Alert System */
  const [alert, setAlert] = useState({
    open: false,
    message: "",
    severity: "",
  });

  const showAlert = (message, severity = "success") => {
    setAlert({ open: true, message, severity });
    setTimeout(() => setAlert({ open: false }), 3000);
  };

  const fetchVendors = async () => {
    try {
      const res = await vendorAPI.getVendors();
      setVendors(res.data || []);
    } catch (err) {
      showAlert("Error loading vendors", "error");
    }
  };

  const fetchNextSrNo = async () => {
    try {
      const res = await vendorAPI.getNextSrNo();
      setVendorForm((prev) => ({ ...prev, sr_no: res.data.next_sr_no }));
    } catch (err) {
      showAlert("Error fetching next SR No", "error");
    }
  };

  useEffect(() => {
    fetchVendors();
  }, []);

  /** Modal Handling */
  const openModal = (vendor = null) => {
    if (vendor) {
      setEditingId(vendor.id);
      setVendorForm({
        sr_no: vendor.sr_no,
        company_name: vendor.company_name,
        vendor_code: vendor.vendor_code,
        website: vendor.website || "",
        addresses: vendor.addresses.length
          ? vendor.addresses
          : [{ address: "", is_primary: true }],
        contacts: vendor.contacts.length
          ? vendor.contacts
          : [{ contact_name: "", phone: "", is_primary: true }],
        emails: vendor.emails.length
          ? vendor.emails
          : [{ email: "", is_primary: true }],
      });
    } else {
      setEditingId(null);
      setVendorForm(emptyVendorForm);
      fetchNextSrNo();
    }
    setOpen(true);
  };

  const closeModal = () => {
    setOpen(false);
    setEditingId(null);
    setVendorForm(emptyVendorForm);
  };

  /** Form Handlers */
  const handleMainField = (e) => {
    setVendorForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleNestedField = (list, index, field, value) => {
    const updated = [...vendorForm[list]];
    updated[index][field] = value;
    setVendorForm((prev) => ({ ...prev, [list]: updated }));
  };

  const addRow = (list) => {
    const newObj =
      list === "addresses"
        ? { address: "", is_primary: false }
        : list === "contacts"
          ? { contact_name: "", phone: "", is_primary: false }
          : { email: "", is_primary: false };

    setVendorForm((prev) => ({
      ...prev,
      [list]: [...prev[list], newObj],
    }));
  };

  const removeRow = (list, index) => {
    let updated = vendorForm[list].filter((_, i) => i !== index);
    if (!updated.length) {
      updated =
        list === "addresses"
          ? [{ address: "", is_primary: true }]
          : list === "contacts"
            ? [{ contact_name: "", phone: "", is_primary: true }]
            : [{ email: "", is_primary: true }];
    }
    setVendorForm((prev) => ({ ...prev, [list]: updated }));
  };

  const setPrimary = (list, index) => {
    setVendorForm((prev) => ({
      ...prev,
      [list]: prev[list].map((item, i) => ({
        ...item,
        is_primary: i === index,
      })),
    }));
  };

  /** Save Handler */
  const onSubmit = async () => {
    try {
      if (editingId) {
        await vendorAPI.updateVendor(editingId, vendorForm);
        showAlert("Vendor updated!");
      } else {
        await vendorAPI.createVendor(vendorForm);
        showAlert("Vendor created!");
      }
      fetchVendors();
      closeModal();
    } catch (err) {
      showAlert("Error while saving", "error");
    }
  };

  /** Delete Vendor */
  const onDelete = async (id) => {
    if (!perms.canDelete()) return showAlert("Not allowed", "error");
    if (!window.confirm("Delete vendor?")) return;
    try {
      await vendorAPI.deleteVendor(id);
      fetchVendors();
      showAlert("Vendor deleted");
    } catch {
      showAlert("Delete failed", "error");
    }
  };

  return (
    <ThemeProvider theme={theme}>
      <Box sx={{ py: 2 }}>
        <Snackbar
          open={alert.open}
          autoHideDuration={4000}
          onClose={() => setAlert((a) => ({ ...a, open: false }))}
          anchorOrigin={{ vertical: "top", horizontal: "center" }}
        >
          <MuiAlert
            onClose={() => setAlert((a) => ({ ...a, open: false }))}
            severity={alert.severity || "info"}
            variant="filled"
            sx={{ width: "100%" }}
          >
            {alert.message}
          </MuiAlert>
        </Snackbar>
        {/* ADD VENDOR BUTTON */}
        <Box
          sx={{
            mb: 3,
            display: "flex",
            justifyContent: "flex-start",
          }}
        >
          {perms.canCreate() && (
            <Button
              variant="contained"
              startIcon={<AddRounded />}
              onClick={() => openModal()}
              sx={{
                px: 2.5,
                py: 1.1,
                boxShadow: `0 8px 20px ${alpha(
                  theme.palette.primary.main,
                  0.25,
                )}`,
              }}
            >
              Add Vendor
            </Button>
          )}
        </Box>

        <Container maxWidth="xl">
          {/* Vendor Table */}
          <Card>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>SR</TableCell>
                    <TableCell>Vendor Name</TableCell>
                    <TableCell>Vendor Code</TableCell>
                    <TableCell>Contacts</TableCell>
                    <TableCell>Emails</TableCell>
                    <TableCell>Addresses</TableCell>
                    <TableCell align="center">Actions</TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {vendors.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={7} align="center">
                        No vendors found
                      </TableCell>
                    </TableRow>
                  )}

                  {vendors.map((v) => (
                    <TableRow key={v.id}>
                      <TableCell>{v.sr_no}</TableCell>
                      <TableCell>{v.company_name}</TableCell>
                      <TableCell>{v.vendor_code}</TableCell>

                      {/* Contacts */}
                      <TableCell>
                        {v.contacts.map((c) => (
                          <Chip
                            key={c.id}
                            label={
                              c.contact_name
                                ? `${c.contact_name} (${c.phone})`
                                : c.phone
                            }
                            size="small"
                            sx={{
                              m: 0.2,
                              bgcolor: c.is_primary
                                ? alpha("#005b82", 0.15)
                                : undefined,
                            }}
                          />
                        ))}
                      </TableCell>

                      {/* Emails */}
                      <TableCell>
                        {v.emails.map((e) => (
                          <Chip
                            key={e.id}
                            label={e.email}
                            size="small"
                            sx={{
                              m: 0.2,
                              bgcolor: e.is_primary
                                ? alpha("#005b82", 0.15)
                                : undefined,
                            }}
                          />
                        ))}
                      </TableCell>

                      {/* Addresses */}
                      <TableCell>
                        {v.addresses.map((a) => (
                          <Chip
                            key={a.id}
                            label={a.address}
                            size="small"
                            sx={{
                              m: 0.2,
                              bgcolor: a.is_primary
                                ? alpha("#005b82", 0.15)
                                : undefined,
                            }}
                          />
                        ))}
                      </TableCell>

                      <TableCell align="center">
                        {perms.canEdit() && (
                          <IconButton size="small" onClick={() => openModal(v)}>
                            <EditRounded fontSize="small" />
                          </IconButton>
                        )}
                        {perms.canDelete() && (
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => onDelete(v.id)}
                          >
                            <DeleteRounded fontSize="small" />
                          </IconButton>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Card>

          {/* Vendor Form Modal */}
          <Dialog open={open} onClose={closeModal} maxWidth="md" fullWidth>
            <DialogTitle>
              {editingId ? "Edit Vendor" : "Create Vendor"}
            </DialogTitle>
            <DialogContent dividers>
              <Box sx={{ display: "grid", gap: 2 }}>
                <TextField
                  label="Vendor Name"
                  name="company_name"
                  value={vendorForm.company_name}
                  onChange={handleMainField}
                  fullWidth
                />
                <TextField
                  label="Vendor Code"
                  name="vendor_code"
                  value={vendorForm.vendor_code}
                  onChange={handleMainField}
                  fullWidth
                />
                <TextField
                  label="Website"
                  name="website"
                  value={vendorForm.website}
                  onChange={handleMainField}
                  fullWidth
                />

                {/* CONTACTS */}
                <Typography variant="subtitle2" fontWeight={700}>
                  Contacts
                </Typography>
                {vendorForm.contacts.map((c, i) => (
                  <Box key={i} sx={{ display: "flex", gap: 1 }}>
                    <TextField
                      label="Name"
                      value={c.contact_name}
                      onChange={(e) =>
                        handleNestedField(
                          "contacts",
                          i,
                          "contact_name",
                          e.target.value,
                        )
                      }
                      fullWidth
                    />
                    <TextField
                      label="Phone"
                      value={c.phone}
                      onChange={(e) =>
                        handleNestedField(
                          "contacts",
                          i,
                          "phone",
                          e.target.value,
                        )
                      }
                      fullWidth
                    />
                    <Button
                      size="small"
                      onClick={() => setPrimary("contacts", i)}
                    >
                      {c.is_primary ? "Primary" : "Set Primary"}
                    </Button>
                    <Button
                      size="small"
                      color="error"
                      onClick={() => removeRow("contacts", i)}
                    >
                      Remove
                    </Button>
                  </Box>
                ))}
                <Button onClick={() => addRow("contacts")}>
                  + Add Contact
                </Button>

                {/* EMAILS */}
                <Typography variant="subtitle2" fontWeight={700}>
                  Emails
                </Typography>
                {vendorForm.emails.map((e, i) => (
                  <Box key={i} sx={{ display: "flex", gap: 1 }}>
                    <TextField
                      label="Email"
                      value={e.email}
                      onChange={(ev) =>
                        handleNestedField("emails", i, "email", ev.target.value)
                      }
                      fullWidth
                    />
                    <Button
                      size="small"
                      onClick={() => setPrimary("emails", i)}
                    >
                      {e.is_primary ? "Primary" : "Set Primary"}
                    </Button>
                    <Button
                      size="small"
                      color="error"
                      onClick={() => removeRow("emails", i)}
                    >
                      Remove
                    </Button>
                  </Box>
                ))}
                <Button onClick={() => addRow("emails")}>+ Add Email</Button>

                {/* ADDRESSES */}
                <Typography variant="subtitle2" fontWeight={700}>
                  Addresses
                </Typography>
                {vendorForm.addresses.map((a, i) => (
                  <Box key={i} sx={{ display: "flex", gap: 1 }}>
                    <TextField
                      label="Address"
                      value={a.address}
                      onChange={(e) =>
                        handleNestedField(
                          "addresses",
                          i,
                          "address",
                          e.target.value,
                        )
                      }
                      fullWidth
                      multiline
                    />
                    <Button
                      size="small"
                      onClick={() => setPrimary("addresses", i)}
                    >
                      {a.is_primary ? "Primary" : "Set Primary"}
                    </Button>
                    <Button
                      size="small"
                      color="error"
                      onClick={() => removeRow("addresses", i)}
                    >
                      Remove
                    </Button>
                  </Box>
                ))}
                <Button onClick={() => addRow("addresses")}>
                  + Add Address
                </Button>
              </Box>
            </DialogContent>

            <DialogActions>
              <Button onClick={closeModal}>Cancel</Button>
              <Button variant="contained" onClick={onSubmit}>
                {editingId ? "Update" : "Create"}
              </Button>
            </DialogActions>
          </Dialog>
        </Container>
      </Box>
    </ThemeProvider>
  );
};

export default VendorTracker;
